// Confirm your email (WEB_PLAN.md §4.2, §5.1), the web twin of iOS VerifyCodeView: the 6-digit code emailed after
// Create account (or after a password sign-in to an account that was never confirmed). Verifying signs in and then
// saves the password chosen at sign-up again right away, because the server drops passwords set before the address
// was proved. That password lives only in memory (auth/pending.ts) and is forgotten once used; a reload loses it on
// purpose and this screen then sends the person back to sign in.

import { MailOpen, Send, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { authErrorMessage } from '@/auth/errors';
import { codeDigits, isCompleteCode, resendSignUpCode, verifySignUp } from '@/auth/flows';
import { clearVerification, currentVerification, rememberEmail } from '@/auth/pending';
import { AuthCardBody, AuthHeader, AuthPage } from './components/AuthCard';
import { CodeInput } from './components/CodeInput';
import { PasswordNotSavedNotice } from './components/PasswordNotSavedNotice';
import { ResendCodeButton } from './components/ResendCodeButton';
import { CodeSentRow, ErrorText, Notice, PrimaryButton, TextButton } from './components/ui';

/** GoTrue's `max_frequency` between two codes for one address. */
const RESEND_AFTER_MS = 30_000;

export default function VerifyCodePage() {
  const navigate = useNavigate();
  // Read once: verifying clears the hand-off, and this screen keeps working with the address it was opened for.
  const [pending] = useState(() => currentVerification());
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [availableAt, setAvailableAt] = useState(() => (pending ? pending.sentAt + RESEND_AFTER_MS : 0));
  const [unsaved, setUnsaved] = useState(false);

  if (!pending) return <Navigate to="/sign-in" replace />;

  const busy = verifying || resending;
  const canVerify = isCompleteCode(codeDigits(code)) && !busy;

  const submit = async (typed?: string) => {
    const entered = codeDigits(typed ?? code);
    if (!isCompleteCode(entered) || busy) return;
    setVerifying(true);
    setError(null);
    setNotice(null);
    try {
      const outcome = await verifySignUp(pending.email, entered);
      if (outcome === 'unsaved') {
        setUnsaved(true);
        setVerifying(false);
      }
      // 'saved': the gate is released and AuthProvider takes over; stay busy until this page goes away.
    } catch (e) {
      setError(authErrorMessage(e));
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (busy || Date.now() < availableAt) return;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await resendSignUpCode(pending.email);
      setAvailableAt(Date.now() + RESEND_AFTER_MS);
      setCode('');
      setNotice('We emailed you a new code.');
    } catch (e) {
      setError(authErrorMessage(e));
    } finally {
      setResending(false);
    }
  };

  const goBack = () => {
    clearVerification();
    rememberEmail(pending.email);
    navigate(pending.origin === 'signUp' ? '/sign-up' : '/sign-in', { replace: true });
  };

  return (
    <AuthPage>
      <AuthHeader
        icon={<MailOpen className="h-6 w-6" aria-hidden />}
        title="Confirm your email"
        subtitle="Enter the 6-digit code we emailed you to confirm your account. It can take a minute; look in spam too."
      />
      {unsaved ? (
        <AuthCardBody>
          <PasswordNotSavedNotice />
        </AuthCardBody>
      ) : (
        <AuthCardBody as="form" onSubmit={() => void submit()}>
          <CodeSentRow destination={pending.email} />
          <CodeInput value={code} onChange={setCode} onComplete={(c) => void submit(c)} disabled={verifying} autoFocus />
          {error ? (
            <ErrorText>{error}</ErrorText>
          ) : (
            notice && <Notice icon={<Send className="h-3.5 w-3.5" aria-hidden />}>{notice}</Notice>
          )}
          <PrimaryButton
            type="submit"
            busy={verifying}
            busyLabel="Verifying…"
            icon={<ShieldCheck className="h-4 w-4" aria-hidden />}
            disabled={!canVerify}
          >
            Verify &amp; continue
          </PrimaryButton>
          <ResendCodeButton availableAt={availableAt} busy={resending} disabled={verifying} onClick={() => void resend()} />
          <TextButton tone="muted" className="self-center" onClick={goBack} disabled={busy}>
            Wrong email? Go back
          </TextButton>
        </AuthCardBody>
      )}
    </AuthPage>
  );
}
